import * as R from 'ramda'
import {
  addMinutes,
  eachHourOfInterval,
  endOfDay,
  formatDate,
  getHours,
  interval,
  isBefore,
  isSameHour,
  isSameMinute,
  set,
  startOfDay,
} from 'date-fns'
import React from 'react'
import { twMerge } from 'tailwind-merge'
import { useIsTouchDevice } from '../../../../hooks'
import { userTimezone } from './helpers'
import { tz, TZDate } from '@date-fns/tz'

export type TimePickerProps = {
  name?: string
  className?: string
  timezone?: string
  date: Date | undefined
  setDate: (date: Date) => void
  disableDates?: (date: Date) => boolean
}

export const TimePicker = ({
  name = 'time',
  className,
  ...props
}: TimePickerProps) => {
  const options = { in: tz(props.timezone ?? userTimezone) }
  const newDate = new TZDate(new Date(), props.timezone ?? userTimezone)
  const visibleDay = props.date ?? newDate
  const hours = eachHourOfInterval(
    interval(
      startOfDay(visibleDay, options),
      endOfDay(visibleDay, options),
      options,
    ),
    options,
  )

  const morningAfternoon = R.partition(
    (date) =>
      isBefore(
        date,
        set(
          visibleDay,
          {
            hours: 12,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          },
          options,
        ),
      ),
    hours,
  )

  const { lastPointerEventWasTouch } = useIsTouchDevice()

  const buttonClasses =
    'text-nowrap px-1 hover:font-bold hover:bg-base-200 rounded hover:text-base-content'

  const disabledClasses =
    'disabled:cursor-default disabled.bg-transparent disabled:hover:bg-transparent hover:disabled:text-base-200 disabled:text-base-100 hover:disabled:font-normal'

  const activeClasses =
    'bg-base-300 text-primary-500 font-bold hover:bg-base-300 hover:text-primary'
  // TODO change this to pointer-coarse:
  const minuteClasses = lastPointerEventWasTouch
    ? 'text-base-500/50'
    : 'text-base-500/30 group-hover:text-base-500 disabled:group-hover:text-base-100'

  return (
    <div className={twMerge('flex flex-col gap-4', className)}>
      <div className='grid grid-cols-[repeat(2,3.5rem_2rem_2rem_2rem)] text-sm'>
        {morningAfternoon.map((hours, i) => (
          <div
            key={'partition' + i}
            className='grid grid-cols-subgrid col-span-4 first:border-r'
            style={{
              gridColumnStart: i * 4 + 1,
            }}>
            <div
              className='col-span-4 row-start-1 text-center text-base-content-muted border-b'
              style={{ gridColumnStart: 1 }}>
              {i ? 'Afternoon' : 'Morning'}
            </div>
            {hours.map((hour, j) => (
              <div
                key={[hour, j].join('-')}
                className={twMerge(
                  'col-span-4 grid grid-cols-subgrid group',
                  props.date &&
                    isSameHour(hour, props.date, options) &&
                    'active',
                )}
                style={{ gridColumnStart: 1, gridRowStart: j + 2 }}>
                {[0, 15, 30, 45].map((minutes, k) => {
                  const time = addMinutes(hour, minutes, options)
                  return (
                    <button
                      key={minutes}
                      type='button'
                      disabled={props.disableDates && props.disableDates(time)}
                      onClick={() =>
                        props.setDate(
                          set(
                            visibleDay,
                            {
                              hours: getHours(hour, options),
                              minutes: minutes,
                              seconds: 0,
                              milliseconds: 0,
                            },
                            options,
                          ),
                        )
                      }
                      className={twMerge(
                        buttonClasses,
                        k > 0 && minuteClasses,
                        props.date &&
                          isSameMinute(time, props.date) &&
                          activeClasses,
                        disabledClasses,
                      )}>
                      {formatDate(time, k === 0 ? 'h aa' : ':mm', options)}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
